import { CATEGORY, emptyConsent } from '@/constants/cookie-registry'

export const CONSENT_COOKIE_NAME = 'renew_consent'

const COOKIE_VERSION = 1
// Chrome caps any cookie at 400 days, so a longer max-age is silently truncated.
// The 3-year retention from the spec applies to the server-side consent log, not to this cookie.
const COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60

function readRawCookie(name) {
  try {
    const prefix = name + '='
    const found = document.cookie.split('; ').find((row) => row.indexOf(prefix) === 0)
    return found ? decodeURIComponent(found.slice(prefix.length)) : null
  } catch (e) {
    return null
  }
}

export function readConsentState() {
  const raw = readRawCookie(CONSENT_COOKIE_NAME)
  if (!raw) return null

  let parsed = null
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    return null
  }

  if (!parsed || typeof parsed !== 'object' || !parsed.cat) return null

  const expiresAt = parsed.exp ? Date.parse(parsed.exp) : NaN
  const isExpired = !isNaN(expiresAt) && expiresAt <= Date.now()

  return {
    id: parsed.id || null,
    expiresAt: parsed.exp || null,
    cookieListVersion: parsed.clv || null,
    bannerVersion: parsed.bv || null,
    isExpired,
    categories: {
      ...emptyConsent(),
      [CATEGORY.ANALYTICS]: Boolean(parsed.cat[CATEGORY.ANALYTICS]),
      [CATEGORY.MARKETING]: Boolean(parsed.cat[CATEGORY.MARKETING]),
    },
  }
}

export function hasValidConsent(state) {
  const current = state === undefined ? readConsentState() : state
  return Boolean(current && !current.isExpired)
}

export function readConsentCategories() {
  const state = readConsentState()
  return hasValidConsent(state) ? state.categories : emptyConsent()
}

export function readConsentId() {
  const state = readConsentState()
  return state ? state.id : null
}

export function writeConsentState({ id, expiresAt, categories, cookieListVersion, bannerVersion }) {
  const payload = {
    v: COOKIE_VERSION,
    id: id || null,
    exp: expiresAt || null,
    clv: cookieListVersion || null,
    bv: bannerVersion || null,
    cat: {
      [CATEGORY.ANALYTICS]: Boolean(categories && categories[CATEGORY.ANALYTICS]),
      [CATEGORY.MARKETING]: Boolean(categories && categories[CATEGORY.MARKETING]),
    },
  }

  try {
    const value = encodeURIComponent(JSON.stringify(payload))
    const secure = location.protocol === 'https:' ? '; secure' : ''
    document.cookie =
      CONSENT_COOKIE_NAME +
      '=' +
      value +
      '; max-age=' +
      COOKIE_MAX_AGE_SECONDS +
      '; path=/; samesite=lax' +
      secure
  } catch (e) {
    return
  }
}

// Rewrites the cookie unchanged so its 400-day cap starts counting again. Chrome, Firefox and
// Safari all measure that cap from the moment the cookie is set, so a returning visitor keeps
// the same chain indefinitely instead of losing it on day 400. Deliberately preserves `exp` as
// it stands, including an expired one: a lapsed consent still has to show the banner, but its
// consent_id has to survive so the next decision continues the chain rather than starting a
// second one for the same person.
export function refreshConsentState() {
  const state = readConsentState()
  if (!state) return false

  writeConsentState({
    id: state.id,
    expiresAt: state.expiresAt,
    categories: state.categories,
    cookieListVersion: state.cookieListVersion,
    bannerVersion: state.bannerVersion,
  })

  return true
}

export function clearConsentState() {
  try {
    document.cookie = CONSENT_COOKIE_NAME + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/'
  } catch (e) {
    return
  }
}
